"use client";

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { TaskThread } from "@/components/missions/task-thread/TaskThread";

type TaskThreadDrawerContextValue = {
  /** Open the task thread for ``taskId`` in an in-window slide-over. */
  openTaskThread: (taskId: string) => void;
};

const TaskThreadDrawerContext = createContext<TaskThreadDrawerContextValue | null>(null);

/**
 * Lets chat embeds open a task thread in a slide-over drawer over the chat,
 * instead of navigating away to Mission Control. ``TaskThread`` is
 * self-contained (fetches by id, live WS updates), so we only supply the id.
 */
export function TaskThreadDrawerProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [mountThread, setMountThread] = useState(false);

  const close = useCallback(() => {
    setOpenTaskId(null);
    setMountThread(false);
  }, []);
  const openTaskThread = useCallback((taskId: string) => {
    setMountThread(false);
    setOpenTaskId(taskId);
  }, []);

  const value = useMemo(() => ({ openTaskThread }), [openTaskThread]);

  return (
    <TaskThreadDrawerContext.Provider value={value}>
      <div className="relative h-full w-full">
        {children}
        <AnimatePresence>
          {openTaskId ? (
            <motion.div
              className="absolute inset-0 z-50 flex justify-end"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              data-testid="task-thread-drawer"
            >
              <button
                type="button"
                aria-label="Close task thread"
                className="absolute inset-0 bg-black/40"
                onClick={close}
              />
              <motion.aside
                className="relative h-full w-[min(460px,100%)] flex-shrink-0 overflow-hidden bg-slate-800 shadow-2xl"
                initial={{ x: 40, opacity: 0.6 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 40, opacity: 0 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                onAnimationComplete={() => {
                  if (openTaskId) setMountThread(true);
                }}
              >
                {mountThread ? (
                  <TaskThread
                    taskId={openTaskId}
                    variant="panel"
                    onClose={close}
                    onOpenMission={(missionId) => {
                      close();
                      navigate(`/missions/${missionId}`);
                    }}
                  />
                ) : null}
              </motion.aside>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </TaskThreadDrawerContext.Provider>
  );
}

/** Open a task thread drawer from a chat embed. Returns ``null`` outside a provider. */
export function useTaskThreadDrawer(): TaskThreadDrawerContextValue | null {
  return useContext(TaskThreadDrawerContext);
}
